import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, relTime } from '../lib/api'

/**
 * Marketing leads captured from the free public tools (SPF / DKIM / DMARC / BIMI checkers and the
 * inbox tester). Every visit logs the domain they checked, the result, and best-effort visitor
 * context — IP, geo, ISP, referer. SuperAdmin only; read-only + CSV export. This is purely
 * analytics over the tool_events table and never touches the sending system.
 */
const TOOLS = ['', 'spf', 'dkim', 'dmarc', 'bimi', 'inbox-tester']
const TOOL_LABEL: Record<string, string> = {
  spf: 'SPF', dkim: 'DKIM', dmarc: 'DMARC', bimi: 'BIMI', 'inbox-tester': 'Inbox tester',
}

export function ToolLeads() {
  const [tool, setTool] = useState('')
  const [country, setCountry] = useState('')
  const [q, setQ] = useState('')
  const [qDebounced, setQDebounced] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 50

  // debounce the free-text search a touch
  function onSearch(v: string) {
    setQ(v); setPage(1)
    clearTimeout((onSearch as any)._t)
    ;(onSearch as any)._t = setTimeout(() => setQDebounced(v), 350)
  }

  const stats = useQuery({ queryKey: ['toolLeads', 'stats'], queryFn: api.toolLeads.stats })
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['toolLeads', 'list', tool, country, qDebounced, page],
    queryFn: () => api.toolLeads.list({ tool, country, q: qDebounced, page, pageSize }),
    placeholderData: (prev) => prev,
  })

  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div>
      <h1>Tool leads</h1>
      <p className="lede">
        Everyone who used a free tool (SPF / DKIM / DMARC / BIMI checker or the inbox tester) — the
        domain they checked, the result, and where they came from. Use it for outreach.
      </p>

      {/* stat tiles */}
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Tile label="Total checks" value={stats.data?.total ?? '—'} />
        <Tile label="Last 7 days" value={stats.data?.last7 ?? '—'} />
        {(stats.data?.by_tool ?? []).slice(0, 5).map((t) => (
          <Tile key={t.label} label={TOOL_LABEL[t.label] ?? t.label} value={t.count} />
        ))}
      </div>

      {/* filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field">
            <span className="section-label">Tool</span>
            <select value={tool} onChange={(e) => { setTool(e.target.value); setPage(1) }}>
              {TOOLS.map((t) => <option key={t} value={t}>{t === '' ? 'All tools' : (TOOL_LABEL[t] ?? t)}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="section-label">Country</span>
            <select value={country} onChange={(e) => { setCountry(e.target.value); setPage(1) }}>
              <option value="">All countries</option>
              {(stats.data?.by_country ?? []).filter((c) => c.label !== 'Unknown').map((c) => (
                <option key={c.label} value={c.label}>{c.label} ({c.count})</option>
              ))}
            </select>
          </label>
          <label className="field" style={{ flex: 1, minWidth: 200 }}>
            <span className="section-label">Search domain or IP</span>
            <input value={q} onChange={(e) => onSearch(e.target.value)} placeholder="example.com or 1.2.3.4" />
          </label>
          <a className="button secondary" href={api.toolLeads.exportUrl({ tool, country })} download>
            Export CSV
          </a>
        </div>
      </div>

      <div className="card flush">
        <div className="row" style={{ justifyContent: 'space-between', padding: '10px 14px' }}>
          <span className="small muted">{isLoading ? 'Loading…' : `${total.toLocaleString()} leads`}{isFetching && !isLoading ? ' · refreshing…' : ''}</span>
          <span className="small muted">Page {page} / {pages}</span>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>When</th><th>Tool</th><th>Domain checked</th><th>Result</th>
              <th>Location</th><th>IP / ISP</th><th>Referer</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((r) => (
              <tr key={r.id}>
                <td className="small muted" title={r.createdAt}>{relTime(r.createdAt)}</td>
                <td><span className="badge">{TOOL_LABEL[r.tool] ?? r.tool}</span></td>
                <td className="mono">{r.query || '—'}{r.selector ? <span className="small muted"> · {r.selector}</span> : null}</td>
                <td>
                  {r.found === false
                    ? <span className="pill warn">not found</span>
                    : r.valid === true
                      ? <span className="pill ok">{r.status ?? 'valid'}</span>
                      : <span className="pill warn">{r.status ?? 'issues'}</span>}
                </td>
                <td className="small">{[r.city, r.region, r.country].filter(Boolean).join(', ') || '—'}</td>
                <td className="small"><span className="mono">{r.clientIp || '—'}</span>{r.isp ? <div className="muted">{r.isp}</div> : null}</td>
                <td className="small muted" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.referer}>{r.referer || '—'}</td>
              </tr>
            ))}
            {!isLoading && (data?.items?.length ?? 0) === 0 && (
              <tr><td colSpan={7} className="small muted" style={{ textAlign: 'center', padding: 28 }}>
                No leads yet — they’ll appear here as people use the free tools.
              </td></tr>
            )}
          </tbody>
        </table>
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end', padding: '12px 14px' }}>
          <button className="secondary small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <button className="secondary small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  )
}

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="card" style={{ minWidth: 130, padding: '12px 16px' }}>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="small muted">{label}</div>
    </div>
  )
}
